import {
  ArrowArcLeft,
  ArrowDown,
  ArrowDownRight,
  ArrowUp,
  CircleNotch,
  DotsThreeVertical,
  Info,
  Minus,
  Pencil,
  Plus,
  StopCircle,
  Swap,
  Trash,
  YoutubeLogo,
} from '@phosphor-icons/react';
import cloneDeep from 'lodash.clonedeep';
import {bool, func, object, string} from 'prop-types';
import {Fragment, useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNum} from '../../../../lib/math.mjs';
import {
  EXERCISE_TYPE,
  MG_PROGRESSION_TYPES,
  RIR_TARGET_REPS_BY_MESO_WEEKS,
  STATUSES,
  STATUSES_FINISHED,
  STATUSES_IN_PROGRESS,
} from '../../../../lib/training.mjs';
import {useMesocycle, useUpdateDay} from '../api.js';
import {
  exerciseJointPainFeedbackText,
  exerciseTypeNamesById,
  LS_KEY_WORKOUT_TUTORIAL_SEEN,
  UNIT_DISPLAY,
} from '../constants.js';
import {useBreakpoint, useExercisesById, useMutationCallback} from '../utils/hooks.js';
import {getFirstDayInProgress, requiresBodyweight} from '../utils/index.js';
import {runAfterAnimations} from '../utils/misc.js';
import storage from '../utils/storage.js';
import useLocalStore from '../utils/useLocalStore.js';
import ExerciseNote from './ExerciseNote.jsx';
import ExerciseSelector from './ExerciseSelector.jsx';
import JointPainFeedbackDialog from './JointPainFeedbackDialog.jsx';
import MesoDayExerciseDuplicateDialog from './MesoDayExerciseDuplicateDialog.jsx';
import MesoDayExerciseMessenger from './MesoDayExerciseMessenger.jsx';
import MesoDayExerciseSet from './MesoDayExerciseSet.jsx';
import MesoDayMuscleGroupButton from './MesoDayMuscleGroupButton.jsx';
import MoveDayExerciseDialog from './MoveDayExerciseDialog.jsx';
import RestDialog from './RestDialog.jsx';
import Centered from './ui/Centered.jsx';
import ConfirmDialog from './ui/ConfirmDialog.jsx';
import Dialog from './ui/Dialog.jsx';
import IconButton from './ui/IconButton.jsx';
import Loading from './ui/Loading.jsx';
import {MenuButton} from './ui/MenuItem.jsx';
import SecondaryBadge from './ui/SecondaryBadge.jsx';
import Video from './Video.jsx';
import WarmupDialog from './WarmupDialog.jsx';

function MessageButton({message, onClick}) {
  return (
    <Centered>
      <button className="btn btn-accent gap-2 shadow-md" onClick={onClick}>
        <Info size={20} className="text-accent-content" />
        <span className="text-accent-content">{message}</span>
      </button>
    </Centered>
  );
}

MessageButton.propTypes = {
  message: string.isRequired,
  onClick: func.isRequired,
};

function createSet(exercise, mesoLength, weekIdx, bodyweight, unit) {
  return {
    position: exercise.sets.length,
    repsTarget: RIR_TARGET_REPS_BY_MESO_WEEKS[mesoLength][weekIdx],
    repsActual: null,
    weightTarget: exercise.sets[0]?.weightTarget,
    weightActual: exercise.sets[0]?.weightActual,
    bodyweight,
    bodyweightAt: bodyweight ? new Date().toISOString() : null,
    unit,
  };
}

export default function MesoDayExercise({
  day,
  dayExercise,
  dayMuscleGroup,
  hasUserDismissed,
  onClickMuscleGroup,
  onOpenRequiredFeedback,
  requiredFeedbackData,
  setDayExerciseToDelete,
}) {
  const exercisesById = useExercisesById();
  const {mesoKey} = useParams();
  const {data: meso} = useMesocycle(mesoKey);

  const {empty, ready} = STATUSES.exercise;
  const exercise = exercisesById[dayExercise.exerciseId];
  const isDesktop = useBreakpoint('desktop');

  const [shouldAddNote, setShouldAddNote] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [restOpen, setRestOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [replaceExerciseOpen, setReplaceExerciseOpen] = useState(false);
  const [jointPainOpen, setJoinPainOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [moveExerciseOpen, setMoveExerciseOpen] = useState(false);
  const [moveExerciseDirection, setMoveExerciseDirection] = useState('');
  const [skipSetsOpen, setSkipSetsOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const setReadynessDialogOpen = useLocalStore(st => st.setReadynessDialogOpen);
  const setRepsInfoOpen = useLocalStore(st => st.setRepsInfoOpen);

  const addSet = useUpdateDay(day.id, {meta: {action: 'addSet'}});
  const removeSet = useUpdateDay(day.id, {meta: {action: 'removeSet'}});
  const skipExercise = useUpdateDay(day.id, {meta: {action: 'skipExercise'}});
  const resetExercise = useUpdateDay(day.id, {meta: {action: 'resetExercise'}});

  const dayReadyOrStarted =
    day.status === STATUSES.day.ready || STATUSES_IN_PROGRESS.day.includes(day.status);

  const canAddSet = dayReadyOrStarted;
  const canRemoveSet = dayReadyOrStarted && dayExercise.sets.length > 1;
  const exerciseType = exercise?.exerciseType;

  const nextExerciseFromSameMuscleGroup =
    day.exercises[dayExercise.position + 1]?.muscleGroupId === dayExercise.muscleGroupId;

  const handleAddSet = useMutationCallback(() => {
    const newState = cloneDeep(day.exercises);
    newState[dayExercise.position].sets.push(
      createSet(dayExercise, meso.weeks.length, day.week, day.bodyweight, meso.unit)
    );
    addSet.mutate({...day, exercises: newState});
  }, [addSet, day, dayExercise, meso.unit, meso.weeks.length]);

  const handleRemoveSet = useMutationCallback(() => {
    const newExercises = cloneDeep(day.exercises);
    newExercises[dayExercise.position].sets.pop();
    removeSet.mutate({...day, exercises: newExercises});
  }, [day, dayExercise.position, removeSet]);

  const handleCloseSkipSets = useMutationCallback(() => setSkipSetsOpen(false));
  const handleSkipExercise = useMutationCallback(() => {
    const newExercises = cloneDeep(day.exercises);
    const newExercise = newExercises[dayExercise.position];

    for (const set of newExercise.sets) {
      if (!set.finishedAt) {
        set.repsActual = -1;
        set.finishedAt = new Date().toISOString();
      }
    }

    skipExercise.mutate({...day, exercises: newExercises}, {onSuccess: handleCloseSkipSets});
  }, [day, dayExercise.position, handleCloseSkipSets, skipExercise]);

  const handleCloseResetExercise = useMutationCallback(() => setResetOpen(false));
  const handleResetExercise = useMutationCallback(() => {
    const newExercises = cloneDeep(day.exercises);
    const newExercise = newExercises[dayExercise.position];

    for (const set of newExercise.sets) {
      if (set.finishedAt) {
        set.finishedAt = null;
      }
    }

    resetExercise.mutate({...day, exercises: newExercises}, {onSuccess: handleCloseResetExercise});
  }, [day, dayExercise.position, handleCloseResetExercise, resetExercise]);

  const handleMoveUp = useCallback(() => {
    setMoveExerciseDirection('up');
    setMoveExerciseOpen(true);
  }, []);

  const handleMoveDown = useCallback(() => {
    setMoveExerciseDirection('down');
    setMoveExerciseOpen(true);
  }, []);

  const handleMoveClose = useCallback(() => {
    setMoveExerciseOpen(false);

    runAfterAnimations(() => {
      setMoveExerciseDirection('');
    });
  }, []);

  const targetWeightLabel =
    exerciseType === EXERCISE_TYPE.bodyweightOnly
      ? 'bodyweight'
      : exerciseType === EXERCISE_TYPE.bodyweightLoadable
      ? '+weight'
      : exerciseType === EXERCISE_TYPE.machineAssistance
      ? 'assistance'
      : 'weight';

  const missingBodyweight =
    dayExercise.status === ready && !day.bodyweight && requiresBodyweight(exerciseType);

  const exerciseDone = STATUSES_FINISHED.exercise.includes(dayExercise.status);
  const dayInProgress = getFirstDayInProgress(meso);
  const showAnotherDayInProgress = dayInProgress && dayInProgress.id !== day.id;
  const jointPainText = exerciseJointPainFeedbackText(exercise?.name);

  const isFirstDexercise = day.week === 0 && day.position === 0 && dayExercise.position === 0;

  const tutorialBlocked =
    missingBodyweight || showAnotherDayInProgress || storage.getItem(LS_KEY_WORKOUT_TUTORIAL_SEEN);

  const showTutorial = isFirstDexercise && !tutorialBlocked;

  const canResetExercise = useMemo(() => {
    return !day.finishedAt && dayExercise.sets?.find(set => set.finishedAt);
  }, [day.finishedAt, dayExercise.sets]);

  const requiredFeedbackDmg = requiredFeedbackData?.dayMuscleGroup;
  const isSameDmgAsRequired = requiredFeedbackDmg?.id === dayMuscleGroup.id;
  const dexRequired = requiredFeedbackData?.needsJointPainDex?.id === dayExercise.id;
  const isCurrentDmgRequired =
    !dexRequired &&
    isSameDmgAsRequired &&
    (requiredFeedbackData?.needsPump ||
      requiredFeedbackData?.needsSoreness ||
      requiredFeedbackData?.needsWorkload);

  const shouldShowFeedbackButton =
    hasUserDismissed && (isCurrentDmgRequired || dexRequired) && dayExercise.sets.length > 0;
  const shouldShowJointPain = isNum(dayExercise.jointPain);

  const firstSetBodyweight = dayExercise.sets?.[0]?.bodyweight;

  const maskSets =
    missingBodyweight ||
    requiredFeedbackDmg ||
    (showAnotherDayInProgress && !day.finishedAt) ||
    day.status === STATUSES.day.pending ||
    addSet.isWorking ||
    removeSet.isWorking;

  const targetRIR = Math.abs(RIR_TARGET_REPS_BY_MESO_WEEKS[meso.weeks.length][day.week]);

  if (!exercise || !dayExercise) {
    return <Loading />;
  }

  return (
    <div
      className={`card bg-base-100 p-4 shadow-sm dark:bg-base-200 ${
        nextExerciseFromSameMuscleGroup
          ? 'mb-0 border-b border-base-200 dark:border-base-300/40'
          : 'mb-4'
      }`}
    >
      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex w-full items-center space-x-3">
            <MesoDayMuscleGroupButton
              disabled={!!requiredFeedbackDmg}
              dayMuscleGroup={dayMuscleGroup}
              onClick={onClickMuscleGroup}
              className="justify-start"
            />
            {meso.progressions[exercise?.muscleGroupId]?.mgProgressionType ===
              MG_PROGRESSION_TYPES.slow && <SecondaryBadge />}
          </div>
          <div className="flex items-center space-x-3">
            {exercise?.youtubeId && (
              <IconButton
                title="See video"
                icon={<YoutubeLogo />}
                onClick={() => setVideoOpen(true)}
              />
            )}

            <IconButton title="Exercise" icon={<DotsThreeVertical weight="bold" />}>
              <MenuButton
                onClick={() => setShouldAddNote(true)}
                label="New note"
                icon={<Pencil size={16} />}
              />

              {canAddSet && (
                <MenuButton onClick={handleAddSet} label="Add set" icon={<Plus size={16} />} />
              )}

              {canRemoveSet && (
                <MenuButton
                  onClick={handleRemoveSet}
                  label="Remove set"
                  icon={<Minus size={16} />}
                />
              )}

              {!day.finishedAt && exerciseType !== EXERCISE_TYPE.bodyweightOnly && (
                <MenuButton
                  onClick={() => setDuplicateOpen(true)}
                  label="Add down set"
                  divider
                  icon={<ArrowDownRight size={16} />}
                />
              )}

              {day.exercises.length > 1 && (
                <Fragment>
                  {dayExercise.position > 0 && (
                    <MenuButton
                      onClick={handleMoveUp}
                      label="Move up"
                      divider
                      icon={<ArrowUp size={16} />}
                    />
                  )}
                  {dayExercise.position + 1 !== day.exercises.length && (
                    <MenuButton
                      onClick={handleMoveDown}
                      label="Move down"
                      divider
                      icon={<ArrowDown size={16} />}
                    />
                  )}
                </Fragment>
              )}

              {canResetExercise && (
                <MenuButton
                  onClick={() => setResetOpen(true)}
                  label="Reset"
                  divider
                  icon={<ArrowArcLeft size={16} />}
                />
              )}

              {!day.finishedAt && (
                <MenuButton
                  onClick={() => setReplaceExerciseOpen(true)}
                  label="Replace"
                  divider
                  icon={<Swap size={16} />}
                />
              )}

              {!exerciseDone && !day.finishedAt && day.status !== STATUSES.day.pending && (
                <MenuButton
                  onClick={() => setSkipSetsOpen(true)}
                  label="End exercise"
                  icon={<StopCircle size={16} />}
                />
              )}

              {/* TODO: Should we allow deleting exercises that are finished? */}
              {day.exercises.length > 1 && (
                <MenuButton
                  onClick={() => setDayExerciseToDelete(dayExercise)}
                  label="Delete exercise"
                  divider
                  className="text-rose-500 hover:bg-rose-100"
                  icon={<Trash size={16} />}
                />
              )}
            </IconButton>
          </div>
        </div>

        <div className="mt-3 flex items-end justify-around sm:mt-2">
          <div className="flex grow flex-col -space-y-0.5">
            {/* Exercise Name */}
            <div className="text-base font-medium leading-5 text-base-content">
              {exercise?.name}
            </div>
            <div className="flex min-h-[28px] items-center justify-between">
              {/* Exercise Type */}
              <div className="text-xs uppercase text-base-content/60">
                <span>{exerciseTypeNamesById[exerciseType]}</span>
                {firstSetBodyweight && exerciseType === EXERCISE_TYPE.bodyweightLoadable && (
                  <span>
                    {' @'} {firstSetBodyweight} {UNIT_DISPLAY.plural[day.unit]} bodyweight
                  </span>
                )}
                {firstSetBodyweight && exerciseType === EXERCISE_TYPE.machineAssistance && (
                  <span>
                    {' @'} {firstSetBodyweight} {UNIT_DISPLAY.plural[day.unit]} bodyweight
                  </span>
                )}
              </div>
              <button
                onClick={shouldShowJointPain ? () => setJoinPainOpen(true) : () => {}}
                className={`btn btn-ghost btn-xs h-[28px] w-fit ${
                  shouldShowJointPain ? 'visible' : 'hidden'
                }`}
              >
                Joint pain{isDesktop && `: ${jointPainText.short[dayExercise.jointPain]}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}

      <div>
        <div className={exercise?.notes.length ? 'mt-2' : ''}>
          <div className="divide-y divide-base-200 dark:divide-base-100">
            {exercise?.notes
              .filter(
                exerciseNote =>
                  !exerciseNote.dayExerciseId || exerciseNote.dayExerciseId === dayExercise.id
              )
              .map(exerciseNote => (
                <ExerciseNote
                  key={`exercise-note-${exerciseNote.id}`}
                  dayExercise={dayExercise}
                  exercise={exercise}
                  exerciseNote={exerciseNote}
                />
              ))}
          </div>
          {/* New Note */}
          <ExerciseNote
            dayExercise={dayExercise}
            exercise={exercise}
            isNewNoteOpen={shouldAddNote}
            onNewNoteClose={() => setShouldAddNote(false)}
          />
        </div>
      </div>

      <MesoDayExerciseMessenger
        dex={dayExercise}
        dmg={dayMuscleGroup}
        hidden={!!requiredFeedbackDmg}
        onShowRest={() => setRestOpen(true)}
        setHelpOpen={setHelpOpen}
        showTutorial={showTutorial}
      />

      {/* Sets section */}
      <div className="relative mt-2">
        {/* Sets grid */}
        {dayExercise.status !== empty && (
          <div className="grid grid-cols-4 gap-y-1.5 desktop:grid-cols-5">
            {/* set col  */}
            <div className="hidden p-2 text-center text-sm font-medium uppercase desktop:col-span-1 desktop:block">
              set
            </div>
            {/* weight col  */}
            <div className="col-span-1 px-1 py-2 text-center text-sm font-medium">
              <span className="uppercase">{targetWeightLabel}</span>
            </div>
            {/* reps rir col  */}
            <div className="col-span-2 flex justify-center">
              {/* reps col  */}
              <div className="p-2 text-center text-sm font-medium uppercase">
                reps<span className="mx-1.5 text-xs">•</span>
                <span
                  className="cursor-pointer underline underline-offset-2"
                  onClick={() => setRepsInfoOpen(true)}
                >
                  {targetRIR} RIR
                </span>
              </div>
            </div>
            {/* action col  */}
            <div className="col-span-1 p-2 text-sm font-medium" />
            {dayExercise.sets.map(set => {
              return (
                <Fragment key={set.id}>
                  <div className="col-span-6 border-b border-base-200 dark:border-base-100 desktop:col-span-7" />
                  <MesoDayExerciseSet
                    key={set.id}
                    set={set}
                    dayExercise={dayExercise}
                    day={day}
                    showTutorial={showTutorial}
                  />
                </Fragment>
              );
            })}
          </div>
        )}

        {/* Translucent backdrop for masking the sets */}
        {maskSets && (
          <div className="absolute inset-0 -mx-4 bg-base-100 opacity-80 dark:bg-base-200" />
        )}

        {shouldShowFeedbackButton && (
          <MessageButton message="Your feedback is required" onClick={onOpenRequiredFeedback} />
        )}

        {missingBodyweight && !showAnotherDayInProgress && (
          <MessageButton
            message="Enter bodyweight to begin"
            onClick={() => setReadynessDialogOpen(true)}
          />
        )}

        {(addSet.isWorking || removeSet.isWorking) && !missingBodyweight && (
          <Centered>
            <div className="flex items-center bg-base-200 px-4 py-2 shadow-md">
              <CircleNotch className="mr-3 animate-spin text-base-content" />
              <p className="text-base-content">
                {addSet.isWorking ? 'Adding set...' : 'Removing set...'}
              </p>
            </div>
          </Centered>
        )}
      </div>

      {/* Dialogs */}
      <Dialog isOpen={videoOpen} onClose={() => setVideoOpen(false)} size="lg">
        <div className="flex items-center justify-center bg-black">
          <Video id={exercise?.youtubeId} onClose={() => setVideoOpen(false)} />
        </div>
      </Dialog>

      <WarmupDialog isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      <RestDialog isOpen={restOpen} onClose={() => setRestOpen(false)} />

      <JointPainFeedbackDialog
        pain={dayExercise.jointPain}
        isOpen={jointPainOpen}
        day={day}
        exercise={dayExercise}
        onClose={() => setJoinPainOpen(false)}
        exerciseName={exercise?.name}
      />

      <ExerciseSelector
        isOpen={replaceExerciseOpen}
        onClose={() => setReplaceExerciseOpen(false)}
        dayExercise={dayExercise}
        day={day}
      />

      <MoveDayExerciseDialog
        isOpen={moveExerciseOpen}
        onClose={handleMoveClose}
        dayExercise={dayExercise}
        direction={moveExerciseDirection}
        exerciseName={exercise?.name}
      />

      <MesoDayExerciseDuplicateDialog
        isOpen={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        dayExercise={dayExercise}
      />

      <ConfirmDialog
        title="End exercise?"
        isOpen={skipSetsOpen}
        onClose={handleCloseSkipSets}
        isLoading={skipExercise.isWorking}
        onConfirm={handleSkipExercise}
        confirmButtonText="End exercise"
        message="This cannot be undone"
        variant="error"
        confirmText="end exercise"
        description="Ending this exercise will mark all unfinished sets as skipped."
      />

      <ConfirmDialog
        isOpen={resetOpen}
        onClose={handleCloseResetExercise}
        isLoading={resetExercise.isWorking}
        onConfirm={handleResetExercise}
        confirmButtonText="Reset Exercise"
        description="This will reset all of the sets in this exercise"
      />
    </div>
  );
}

MesoDayExercise.propTypes = {
  day: object.isRequired,
  dayExercise: object.isRequired,
  dayMuscleGroup: object.isRequired,
  hasUserDismissed: bool.isRequired,
  onClickMuscleGroup: func.isRequired,
  onOpenRequiredFeedback: func.isRequired,
  requiredFeedbackData: object,
  setDayExerciseToDelete: func.isRequired,
};
